import { useState } from "react";
import { format, parseISO, isValid } from "date-fns";
import { CalendarIcon, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";

export const triggerCls = "mt-1 w-full inline-flex items-center justify-between gap-2 rounded border border-hairline bg-paper px-3 py-2 text-sm text-left focus:outline-none focus:ring-2 focus:ring-brand disabled:opacity-60 disabled:cursor-not-allowed";
const displayFmt = "d MMM yyyy";

const toDate = (iso) => {
  if (!iso) return undefined;
  const d = parseISO(iso);
  return isValid(d) ? d : undefined;
};
const toIso = (date) => (date ? format(date, "yyyy-MM-dd") : null);

/** Single-date picker — click-to-pick only, emits ISO 'YYYY-MM-DD' (or null). No free-typing. */
export function DatePicker({ value, onChange, placeholder = "Pick a date…", testId, disabled }) {
  const [open, setOpen] = useState(false);
  const selected = toDate(value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testId} disabled={disabled} className={triggerCls}>
          <span className={selected ? "text-ink" : "text-faint"}>{selected ? format(selected, displayFmt) : placeholder}</span>
          <CalendarIcon className="h-3.5 w-3.5 shrink-0 text-faint" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 bg-paper border-hairline" align="start">
        <Calendar
          mode="single"
          selected={selected}
          onSelect={(d) => { onChange(toIso(d)); setOpen(false); }}
          initialFocus
          data-testid={testId ? `${testId}-calendar` : undefined}
        />
        {selected && (
          <div className="border-t border-hairline p-2">
            <button type="button" data-testid={testId ? `${testId}-clear` : undefined}
              onClick={() => { onChange(null); setOpen(false); }}
              className="w-full inline-flex items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs text-ink-soft hover:text-ink transition-colors">
              <X className="h-3 w-3" /> Clear
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

/**
 * Date-range picker — used for fields like Shoot Dates that can span multiple days.
 * A single click (no second click) is a valid single-day selection; clicking a second,
 * later day extends it into a range. Emits {start, end} as ISO strings (end === null when
 * only a single day is picked) — never a free-typed string.
 */
export function DateRangePicker({ value, onChange, placeholder = "Pick a date or range…", testId, disabled }) {
  const [open, setOpen] = useState(false);
  const from = toDate(value?.start);
  const to = toDate(value?.end);
  const label = from
    ? (to && toIso(to) !== toIso(from) ? `${format(from, displayFmt)} \u2192 ${format(to, displayFmt)}` : format(from, displayFmt))
    : placeholder;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testId} disabled={disabled} className={triggerCls}>
          <span className={from ? "text-ink" : "text-faint"}>{label}</span>
          <CalendarIcon className="h-3.5 w-3.5 shrink-0 text-faint" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 bg-paper border-hairline" align="start">
        <Calendar
          mode="range"
          selected={{ from, to }}
          onSelect={(range) => onChange({ start: toIso(range?.from), end: toIso(range?.to) })}
          numberOfMonths={1}
          initialFocus
          data-testid={testId ? `${testId}-calendar` : undefined}
        />
        <div className="flex items-center justify-between border-t border-hairline p-2">
          <span className="text-[10px] text-faint px-1">Pick one day, or a start &amp; end for a range</span>
          <div className="flex items-center gap-2">
            {from && (
              <button type="button" data-testid={testId ? `${testId}-clear` : undefined}
                onClick={() => onChange({ start: null, end: null })}
                className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-ink-soft hover:text-ink transition-colors">
                <X className="h-3 w-3" /> Clear
              </button>
            )}
            <button type="button" data-testid={testId ? `${testId}-done` : undefined}
              onClick={() => setOpen(false)}
              className="rounded-md bg-brand px-3 py-1 text-xs font-medium text-ink hover:bg-brand-dim">
              Done
            </button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
